import React, { useEffect, useState } from "react";
import { formatDate, formatToZMW } from "../../../helpers";
import { useOutletContext } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { calculateTrialBalance } from "../../../utils/reportCalculations";

export default function TrialBalance({ rdata, startDate, endDate }) {
  const [trialBalanceData, setTrialBalanceData] = useState([]);
  const [isBalanced, setIsBalanced] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(true);
  const { allData } = useOutletContext();

  useEffect(() => {
    if (!rdata?.bills) return;

    const result = calculateTrialBalance(rdata, allData);
    setTrialBalanceData(result.rows);
    setIsBalanced(result.isBalanced);
    setDiagnosis(result.diagnosis || null);
    setLoading(false);
  }, [rdata, allData]);

  let totalDebit = 0;
  let totalCredit = 0;

  return (
    <div className="container mx-auto p-4">
      {loading ? (
        <div className="flex justify-center items-center h-full w-full text-blue-950 text-5xl p-5 m-5">
          <FaSpinner size={50} className="spinner" />
        </div>
      ) : (
        <>
          <div className="text-center">
            <h2 className="font-bold text-2xl">{allData.council[0]?.name.toUpperCase()}</h2>
            <h2 className="font-bold text-2xl my-3">TRIAL BALANCE</h2>
            <h3 className="text-xl font-bold">
              From {formatDate(startDate)} to {formatDate(endDate)}
            </h3>
          </div>

          <table className="table-auto w-full mt-4 border-collapse border border-gray-300 shadow-lg rounded-lg">
            <thead className="bg-blue-950 text-white text-left p-2">
              <tr className="p-2">
                <th className="px-4 py-2 border-b border-gray-300">Code</th>
                <th className="px-4 py-2 border-b border-gray-300">Description</th>
                <th className="px-4 py-2 border-b border-gray-300 text-right">Debit</th>
                <th className="px-4 py-2 border-b border-gray-300 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {trialBalanceData.map((entry, index) => {
                totalDebit += entry.debit || 0;
                totalCredit += entry.credit || 0;
                const isSuspense = entry.code === '999999';
                return (
                  <tr
                    key={`${entry.code}-${index}`}
                    className={`text-gray-700 hover:bg-gray-100 ${
                      isSuspense ? "bg-amber-50 font-semibold" : index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-3 border-b border-gray-300">{entry.code}</td>
                    <td className="px-4 py-3 border-b border-gray-300">{entry.desc}</td>
                    <td className="px-4 py-3 border-b border-gray-300 text-right">
                      {formatToZMW((entry.debit || 0).toFixed(2))}
                    </td>
                    <td className="px-4 py-3 border-b border-gray-300 text-right">
                      {formatToZMW((entry.credit || 0).toFixed(2))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="text-gray-800 font-semibold bg-gray-200">
                <th colSpan="2" className="px-4 py-3 text-left border-t border-gray-300">
                  Total
                </th>
                <th className="px-4 py-3 text-right border-t border-gray-300">
                  {formatToZMW(totalDebit.toFixed(2))}
                </th>
                <th className="px-4 py-3 text-right border-t border-gray-300">
                  {formatToZMW(totalCredit.toFixed(2))}
                </th>
              </tr>
            </tfoot>
          </table>

          <div className={`mt-4 ${isBalanced ? "text-blue-500" : "text-red-500"}`}>
            Trial Balance is {isBalanced ? "Balanced" : "Not Balanced"}
          </div>

          {diagnosis && Math.abs(diagnosis.residualBeforeSuspense || 0) >= 0.05 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-slate-700">
              <p className="font-semibold mb-2">Imbalance analysis (before suspense)</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Difference: {formatToZMW(diagnosis.residualBeforeSuspense)}</li>
                <li>Cashbook income (excl. opening/transfers): {formatToZMW(diagnosis.cashIncome)}</li>
                <li>Receipts total: {formatToZMW(diagnosis.receiptTotal)}</li>
                <li>Income gap (cash − receipts): {formatToZMW(diagnosis.incomeGap)}</li>
                <li>Cashbook expense (excl. transfers): {formatToZMW(diagnosis.cashExpense)}</li>
                <li>Supplier payments: {formatToZMW(diagnosis.supplierPayments)}</li>
                <li>Expense gap (cash − payments): {formatToZMW(diagnosis.expenseGap)}</li>
              </ul>
              <p className="mt-2">
                Common causes: cash deleted without deleting the receipt/payment, duplicate vouchers,
                or imprest/journal cashbook entries with no matching receipt.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
